import { useState } from 'react'
import type { InventorySort } from '@kronos/domain'
import { Glyph } from './glyphs'

/**
 * Search, sort, and gallery-or-list. The view is remembered per browser — the
 * person who prefers the list prefers it every time — and only that: losing
 * it (a private window, cleared storage) just means the gallery.
 */
export type InventoryLayout = 'grid' | 'list'

const KEY = 'kronos.inventory.layout'

export function useInventoryLayout(): [InventoryLayout, (next: InventoryLayout) => void] {
  const [layout, setLayout] = useState<InventoryLayout>(() => {
    try {
      return window.localStorage.getItem(KEY) === 'list' ? 'list' : 'grid'
    } catch {
      return 'grid'
    }
  })
  const choose = (next: InventoryLayout): void => {
    setLayout(next)
    try {
      window.localStorage.setItem(KEY, next)
    } catch {
      // Storage refused: the choice still holds for this visit.
    }
  }
  return [layout, choose]
}

const SORTS: readonly { readonly value: InventorySort; readonly label: string }[] = [
  { value: 'name', label: 'Name, A to Z' },
  { value: 'shelf', label: 'Most on the shelf' },
  { value: 'recent', label: 'Recently received' },
]

export function InventoryToolbar({
  query,
  onQuery,
  sort,
  onSort,
  layout,
  onLayout,
}: {
  query: string
  onQuery: (q: string) => void
  sort: InventorySort
  onSort: (s: InventorySort) => void
  layout: InventoryLayout
  onLayout: (l: InventoryLayout) => void
}): JSX.Element {
  return (
    <div className="inv-toolbar">
      <input
        type="search"
        className="inv-search"
        placeholder="Search by name, part number or supplier"
        aria-label="Search items"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
      />
      <label className="inv-sort">
        <span className="inv-sort__label">Sort</span>
        <select value={sort} onChange={(e) => onSort(e.target.value as InventorySort)} aria-label="Sort items">
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
      <div className="inv-segmented" role="group" aria-label="View">
        <button type="button" aria-pressed={layout === 'grid'} aria-label="Gallery" onClick={() => onLayout('grid')}>
          <Glyph name="grid" />
          <span className="inv-segmented__text">Gallery</span>
        </button>
        <button type="button" aria-pressed={layout === 'list'} aria-label="List" onClick={() => onLayout('list')}>
          <Glyph name="list" />
          <span className="inv-segmented__text">List</span>
        </button>
      </div>
    </div>
  )
}
